/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-search-view-component.ts
 *
 * Created by rwilliams on 8/3/17.
 */

function BrandingSearchViewController(this: JS5Object,
                                      _: JS5Object,
                                      $routeParams: JS5Object,
                                      $location: JS5Object,
                                      uiGridConstants: JS5Object,
                                      PermissionsService: JS5Object,
                                      BrandingAssetService: JS5Object,
                                      ContentEditService: JS5Object,
                                      ModalService: JS5Object,
                                      NotificationService: JS5Object) {

    let showHidden: boolean = false;
    let seasonsVolumes: string = null;
    let seasonVolumeId: number = null;
    let treeData: JS5Object[];

    const assetsKey = () => {
        switch (this.type) {
            case 'artwork':
            case 'preRolls':
            case 'postRolls':
            case 'watermarks':
                return 'assets';

            case 'breakPrePostRolls':
                return 'breakPrePostRolls';

            default:
                console.error(`unrecognized branding asset type ${this.type}`);
                return '';
        }
    };

    const acceptProfileKey = () => {
        switch (this.type) {
            case 'artwork':
            case 'preRolls':
            case 'postRolls':
            case 'breakPrePostRolls':
                return 'acceptProfile';

            case 'watermarks':
                return 'videoAcceptProfile';

            default:
                console.error(`unrecognized branding asset type ${this.type}`);
                return '';
        }
    };


    //======//
    // Grid //
    //======//

    let gridColumns = [
        {
            field: 'name',
            displayName: 'Name',
            cellTooltip: true,
            cellTemplate: 'components/branding/searchView/cellTemplates/branding-tree-template.html',
            enableSorting: false
        }
    ];

    const addBaseContainerAssets = (containerAssets: JS5Object[]) => {
        // add assets which don't belong to any season or
        // volume first; sort them by the accept profile name
        _.chain(containerAssets)
            .filter((asset: JS5Object) => !asset.seasonName && !asset.volumeName)
            .sortBy(acceptProfileKey())
            .forEach((asset: JS5Object) => {
                asset.$$treeLevel = 1;
                treeData.push(asset);
            })
            .value();
    };

    const addChildContainerAssets = (containerAssets: JS5Object[], childContainerNameKey: string,
                                     childContainerName: string) => {
        _.chain(containerAssets)
            .filter((asset: JS5Object) => asset[childContainerNameKey] === childContainerName)
            .sortBy(acceptProfileKey())
            .forEach((asset: JS5Object) => {
                asset.$$treeLevel = 2;
                treeData.push(asset);
            })
            .value();
    };

    const addChildContainers = (childContainerNameKey: string, containerAssets: JS5Object[]) => {
        // add season child containers, their assets,
        // volume containers and their assets, all sorted

        // `childContainerNamesKey` is either "seasonName" or
        // "volumeName"
        _.chain(containerAssets)
            .filter((asset: JS5Object) => !!asset[childContainerNameKey])
            .map(childContainerNameKey)
            .uniq()
            .sortBy(/* No key: => just sort the array */)
            .forEach((childContainerName: string) => {
                // add the child container itself to level 1
                treeData.push({
                    name: childContainerName,
                    $$treeLevel: 1
                });

                // add this child container's assets to level 2
                addChildContainerAssets(containerAssets, childContainerNameKey, childContainerName);
            })
            .value();
    };

    const addOnlySeasonVolumeAssets = (containerAssets: JS5Object) => {
        let seasonOrVolumeIdKey = seasonsVolumes === 'seasons' ? 'seasonId' : 'volumeId';

        _.chain(containerAssets)
            .filter((asset: JS5Object) => asset[seasonOrVolumeIdKey] === $routeParams[seasonOrVolumeIdKey])
            .sortBy(acceptProfileKey())
            .forEach((asset:JS5Object) => {
                asset.$$treeLevel = 1;
                treeData.push(asset);
            })
            .value();
    };

    const buildTreeView = (containers: JS5Object[]) => {
        this.gridOptions.data = treeData = <JS5Object[]>[];

        _.chain(containers)
            .sortBy('name')
            .forEach((container: JS5Object) => {
                container.$$treeLevel = 0;
                treeData.push(container);

                const containerAssets = container[assetsKey()];

                if (!this.isSeasonVolume) {
                    addBaseContainerAssets(containerAssets);
                    _.forEach(['seasonName', 'volumeName'], (childContainerNameKey: string) => {
                        addChildContainers(childContainerNameKey, containerAssets);
                    });
                } else {
                    addOnlySeasonVolumeAssets(containerAssets);
                }
            })
            .value();

        return treeData;
    };

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: false,
        enableColumnResizing: false,
        enablePaginationControls: false,
        useExternalPagination: false,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableSorting: false,
        enableFiltering: false,
        showTreeExpandNoChildren: false,
        showHeader: true,
        rowHeight: 26,
        treeRowHeaderBaseWidth: 24,
        multiSelect: false,
        columnDefs: gridColumns,
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
        }
    };

    const editBrandingAssetContainer = (container: JS5Object) => {
        if (!_.get(this, 'permissions.read')) {
            NotificationService.createWarningNotification('You do not have permission to view branding assets.');
            return;
        }

        if (container.$$treeLevel === 0 && !container.hidden) {
            const basePath = `${this.baseUrl}/edit/${$routeParams.id}/`;
            const seasonVolumePath = this.isSeasonVolume ? `${seasonsVolumes}/edit/${seasonVolumeId}/` : ``;
            const containerPath = `${this.type}/edit/${container.id}`;
            $location.url(basePath + seasonVolumePath + containerPath);
        }
    };

    this.rowDoubleClick = (entity: JS5Object) => {
        editBrandingAssetContainer(entity);
    };

    this.getAssetAcceptProfile = (asset: JS5Object) => {
        return asset[acceptProfileKey()];
    };


    //======//
    // Data //
    //======//

    const getBrandingConfig = () => {
        return {
            id: $routeParams.id,
            seasonId: $routeParams.seasonId,
            volumeId: $routeParams.volumeId,
            type: this.type,
            isShow: this.groupName !== 'networks',
            groupName: this.groupName
        };
    };

    const getData = () => {
        this.loadingIndicator.show = true;
        this.isBreakPrePostRoll = this.type === 'breakPrePostRolls';

        if (this.isSeasonVolume) {
            ContentEditService.setBrandingBreadcrumbs();
        }
        BrandingAssetService.getBrandingAssetContainers(getBrandingConfig(), $routeParams.id, showHidden)
            .then((containers: JS5Object[]) => {
                if ($routeParams.seasonId) {
                    seasonsVolumes = 'seasons';
                    seasonVolumeId = $routeParams.seasonId;
                } else if ($routeParams.volumeId) {
                    seasonsVolumes = 'volumes';
                    seasonVolumeId = $routeParams.volumeId;
                }

                this.loadingIndicator.show = false;
                buildTreeView(containers);
                this.gridApi.selection.clearSelectedRows();
            });
    };


    //=========//
    // Toolbar //
    //=========//

    const enableNewButton = () => {
        return (
            this.gridApi.selection.getSelectedCount() === 0 &&
            _.get(this, 'permissions.write')
        );
    };

    const enableEditButtons = () => {
        return (
            this.gridApi.selection.getSelectedCount() === 1 &&
            this.gridApi.selection.getSelectedRows()[0].$$treeLevel === 0 &&
            _.get(this, 'permissions.write')
        );
    };

    const isSelectedHidden = () => {
        const exactlyOneSelected = this.gridApi.selection.getSelectedCount() === 1;
        const selectedItemArray = this.gridApi.selection.getSelectedRows();
        const selectedItemIsHidden = _.get(selectedItemArray, '[0].hidden');

        return exactlyOneSelected && selectedItemIsHidden;
    };

    const toggleBrandingAssetContainerVisibility = (container: JS5Object) => {
        BrandingAssetService.toggleBrandingAssetContainerVisibility(getBrandingConfig(), container)
            .then(() => {
                this.gridApi.selection.clearSelectedRows();
                getData();
            });
    };

    const openConfirmationModal = (action: string, resultHandler: JS5Function) => {
        let brandName = this.gridApi.selection.getSelectedRows()[0].name;
        ModalService.showMessageModal({
            messageText: `Are you sure you want to ${action} ${brandName} ?`,
            pullCancelLeft: true,
            title: `${action.charAt(0).toUpperCase()}${action.slice(1)} ${this.type}`,
            showOK: true,
            okText: 'Yes',
            windowClass: 'confirm-modal-sm'
        }, resultHandler);
    };

    const showHideUnhideConfirmationModal = (title: string) => {
        openConfirmationModal(title, () => {
            toggleBrandingAssetContainerVisibility(this.gridApi.selection.getSelectedRows()[0]);
        });
    };

    this.toolbarButtonDefs = [
        {
            label: 'New',
            enabled: () => enableNewButton(),
            onClick: () => {
                let seasonVolumePart = '';

                if (seasonVolumeId) {
                    seasonVolumePart = `${seasonsVolumes}/edit/${seasonVolumeId}/`;
                }

                let newUrl = `${this.baseUrl}/edit/${$routeParams.id}/${seasonVolumePart}${this.type}/edit/-1`;
                $location.url(newUrl);
            }
        },
        {
            label: 'Edit',
            enabled: () => enableEditButtons(),
            onClick: () => {
                const container = this.gridApi.selection.getSelectedRows()[0];
                editBrandingAssetContainer(container);
            }
        },
        {
            label: 'Hide',
            enabled: () => enableEditButtons() && !isSelectedHidden(),
            onClick: () => {
                showHideUnhideConfirmationModal('Hide');
            }
        },
        {
            label: 'Unhide',
            enabled: () => enableEditButtons() && isSelectedHidden(),
            onClick: () => {
                showHideUnhideConfirmationModal('Unhide');
            }
        }
    ];

    this.toolbarCheckboxDefs = [
        {
            label: 'Show Hidden',
            onUpdate: (newValue: boolean) => {
                showHidden = newValue;
                getData();
            },
            isChecked: showHidden,
        }
    ];


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        PermissionsService.getReadWritePermissions(this.permissionViewState)
            .then((permissions: JS5Object) => {
                this.permissions = permissions;
                getData();
            });
    };

}

angular.module('prism-ui.components.branding').component('brandingSearchView', {
    templateUrl: 'components/branding/searchView/branding-search-view-template.html',
    controller: BrandingSearchViewController,
    bindings: {
        isAdmin: '<',
        permissionViewState: '<',
        groupName: '<',
        type: '<',
        baseUrl: '<',
        isSeasonVolume: '<'
    }
});
